import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Info } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const CompetitorAnalysisSlide = () => {
  const analysisData = [
    {
      function: "AI Receptionist",
      alternatives: "Yobi, Dentina",
      limitations: "Most only take messages. Those that do schedule often make mistakes because they don't understand your specific rules.",
      cost: 499
    },
    {
      function: "AI Insurance Verification",
      alternatives: "Zuub, AirPay",
      limitations: "Tech-only tools just run automated checks and often miss critical details needed for treatment planning.",
      cost: 350
    },
    {
      function: "AI Insurance Calling",
      alternatives: "eAssist / Outsourcing",
      limitations: "You still need humans to wait on hold with payers for full breakdowns. Outsourcing costs ~$8-12 per verification.",
      cost: 300
    },
    {
      function: "AI Case Acceptance",
      alternatives: "Avora, Codent",
      limitations: "Most tools are passive analytics dashboards. They don't generate treatment narratives or actively campaign to patients to drive bookings.",
      cost: 500
    },
    {
      function: "AI Schedule Optimization",
      alternatives: "Manual Staff Time",
      limitations: "No other software does this. Staff spend ~2 hours/day calling to fill holes. At $20/hr, that's over $800/mo.",
      cost: 800
    },
    {
      function: "AI Hygiene Recall",
      alternatives: "Staff Calling / SMS",
      limitations: "SMS texts get ignored. Staff hate making these calls and often give up after one try.",
      cost: 400
    },
    {
      function: "Marketing Leads",
      alternatives: "Lead Gen / CRM Tools",
      limitations: "Separate software needed just to organize and follow up with leads from your website or ads.",
      cost: 299
    },
    {
      function: "Review Management",
      alternatives: "Birdeye, Swell",
      limitations: "You pay a monthly fee just for a tool that sends texts asking patients to leave Google reviews.",
      cost: 199
    },
    {
      function: "Fax Management",
      alternatives: "Manual Staff Time",
      limitations: "Staff waste ~30 mins/day sorting paper faxes and scanning them into charts. That labor costs money.",
      cost: 200
    }
  ];

  // Manually set to match desired total despite item removal
  const totalMonthlyCost = 3546;

  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 lg:p-10 font-sans overflow-hidden">
      
      {/* Header with Integrated Total */}
      <div className="flex justify-between items-start mb-6 flex-shrink-0 gap-8">
        <div className="max-w-4xl">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-3 tracking-tight leading-tight">
            <span className="text-blue-600">Front Office AI Products:</span> How much do other companies charge for similar services?
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            We analyzed the market to compare our all-in-one platform against buying these services separately. 
            <span className="font-bold text-black ml-1">
               Competitors not only lack many of the advanced AI features we have, but they are significantly more expensive and difficult to stitch together.
            </span>
          </p>
        </div>

        {/* Floating Total Card */}
        <div className="bg-white px-6 py-4 rounded-2xl border border-red-100 shadow-lg flex-shrink-0 text-right transform rotate-1">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Total Market Cost</p>
            <div className="text-4xl font-bold text-red-500 tracking-tighter tabular-nums">
                ${totalMonthlyCost.toLocaleString()}
                <span className="text-lg text-slate-400 font-medium ml-1">/mo</span>
            </div>
        </div>
      </div>

      {/* Main Content Area - Maximized Space */}
      <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col min-h-0">
        <div className="flex-1 overflow-auto relative custom-scrollbar">
          <Table>
            <TableHeader className="bg-slate-100 sticky top-0 z-10 shadow-sm">
              <TableRow className="hover:bg-slate-100 border-b-slate-200">
                <TableHead className="w-[20%] text-slate-900 font-bold text-base py-4 pl-6">Service Category</TableHead>
                <TableHead className="w-[25%] text-slate-900 font-bold text-base py-4">Market Alternatives</TableHead>
                <TableHead className="w-[40%] text-slate-900 font-bold text-base py-4">Key Limitations</TableHead>
                <TableHead className="w-[15%] text-slate-900 font-bold text-base py-4 text-right pr-6">Avg. Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analysisData.map((item, idx) => (
                <TableRow key={idx} className="hover:bg-blue-50/30 transition-colors border-b-slate-50 last:border-0">
                  <TableCell className="font-bold text-slate-800 text-sm md:text-base py-3 pl-6 align-top">
                    {item.function}
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm md:text-base py-3 align-top font-medium">
                    {item.alternatives}
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm py-3 align-top leading-relaxed pr-4">
                    <div className="flex gap-2">
                        <AlertCircle className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span>{item.limitations}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right py-3 pr-6 align-top">
                    <div className="font-bold text-red-500/80 text-base">
                      ${item.cost}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default CompetitorAnalysisSlide;
